'use client';
import React, { FC, useEffect } from 'react';
import MuxPlayer, { MuxPlayerRefAttributes } from '@mux/mux-player-react';
import {
  _getValidatedStartTime,
  MediaCollectionVideoPreviewProps,
  MediaCollectionVideoProps,
} from './utils';
import MediaCollectionVideoPreview from './MediaCollectionVideoPreview';
import { Errors, Loader } from '@/components';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { ERROR_KINDS } from '@/core/constants';
import { formatTokens } from '@/lib/api/playbackService/hooks/useTokenSetVault';
import { ANONYMOUS } from '@/utils/dataLayer';
import {
  ASSET_TYPES,
  generateMuxPlaybackEvents,
} from '@/utils/videoPlayerHelpers';

export const PLAYER_PLACEHOLDER_CONTAINER_STYLES =
  'flex min-h-60 lg:min-h-[30rem] items-center justify-center';

// for streaming signed mux assets
const MediaCollectionVideo: FC<MediaCollectionVideoProps> = ({
  muxVideo,
  title,
  seriesTitle,
  onVideoComplete,
  onVideoProgress,
  refetchTokens,
  playbackServiceVideoId,
  startTime,
  userUuid,
  tokenSet,
  shouldAutoplay = true,
  setIsVideoPlaying,
}) => {
  const INTERVAL_TIME = 5000; // 5 seconds
  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.COURSE_PLAYER_ASSET_STREAMING_FAILURE,
  );
  useEffect(() => {
    const interval = setInterval(() => {
      const el = _findMuxPlayerElement();
      if (el?.currentTime) {
        onVideoProgress(el.currentTime);
      }
    }, INTERVAL_TIME);
    return () => clearInterval(interval);
  }, [onVideoProgress]);

  if (!muxVideo?.muxVideoUpload || !tokenSet) {
    return (
      <div
        className={PLAYER_PLACEHOLDER_CONTAINER_STYLES}
        data-testid="media-collection-video-error-container"
      >
        <Errors errors={errors} heading={heading} />
      </div>
    );
  }

  if (tokenSet.token.loading) {
    return (
      <div
        className={PLAYER_PLACEHOLDER_CONTAINER_STYLES}
        data-testid="media-collection-video-loading-container"
      >
        <Loader loading />
      </div>
    );
  }

  const { duration } = muxVideo.muxVideoUpload;
  const validatedStartTime = _getValidatedStartTime(startTime, duration);

  function refetchTokenSet() {
    // note: we will limit retry attempts to 5 per mux asset to prevent hammering playback service
    // with requests if we somehow receive a barrage of error events from the player
    // see useTokenSetVault for more information
    if (tokenSet?.playbackId) {
      refetchTokens(tokenSet.playbackId, true);
    }
  }

  const eventProps = generateMuxPlaybackEvents(
    {
      title,
      duration,
      playbackServiceId: playbackServiceVideoId,
      isSigned: true,
      type: ASSET_TYPES.VIDEO,
      viewerId: userUuid || ANONYMOUS,
    },
    {
      error: refetchTokenSet,
      ended: onVideoComplete,
    },
    setIsVideoPlaying,
  );

  return (
    <div
      className="mx-auto max-w-[1200px]"
      data-testid="media-collection-video-container"
    >
      <MuxPlayer
        streamType="on-demand"
        style={{
          aspectRatio: 16 / 9,
        }}
        title={title}
        playbackId={tokenSet.playbackId}
        startTime={validatedStartTime}
        metadata={{
          video_id: playbackServiceVideoId,
          video_title: title,
          viewer_user_id: userUuid,
          env_key: process.env.NEXT_PUBLIC_MUX_ENV_KEY,
          player_name: 'Web Course Video Player',
          // If updates made to the player, update me with either a minor or major version bump
          player_version: '1.0',
          video_content_type: 'episode',
          video_producer: 'Lovevery',
          video_series: seriesTitle,
        }}
        tokens={formatTokens(tokenSet)}
        autoPlay={shouldAutoplay}
        {...eventProps}
      />
    </div>
  );
};

export function isMuxPlayerPaused() {
  const muxPlayerEl = _findMuxPlayerElement();
  return muxPlayerEl?.paused;
}

export function pauseMuxPlayer() {
  const muxPlayerEl = _findMuxPlayerElement();
  muxPlayerEl?.pause();
}

export function playMuxPlayer() {
  const muxPlayerEl = _findMuxPlayerElement();
  muxPlayerEl?.play();
}

function _findMuxPlayerElement() {
  return document?.querySelector<MuxPlayerRefAttributes>('mux-player');
}

export const MediaCollectionVideoTemplateAssignment: FC<
  MediaCollectionVideoPreviewProps | MediaCollectionVideoProps
> = (props) => {
  const { isSigned } = props;
  if (isSigned) {
    return <MediaCollectionVideo {...(props as MediaCollectionVideoProps)} />;
  }

  return (
    <MediaCollectionVideoPreview
      {...(props as MediaCollectionVideoPreviewProps)}
    />
  );
};

export default MediaCollectionVideoTemplateAssignment;
